@if (isLoading()) {
  <div class="flex justify-center items-center h-64">
    <mat-spinner diameter="48" />
  </div>
} @else {
  <div class="p-5">
    <app-breadcrumb [items]="breadcrumbItems()" />

    <!-- Header -->
    <div class="flex justify-between items-center mb-5">
      <h1 class="text-2xl font-medium">
        @if (isEditMode() && accommodationTitle()) {
          {{ accommodationTitle() }}
        } @else {
          {{ 'waters.accommodations.breadcrumb.create' | translate }}
        }
      </h1>
      <div class="flex gap-2">
        @if (isEditMode()) {
          <button
            mat-stroked-button
            color="warn"
            class="!min-w-0 !px-2"
            [disabled]="isDeleting() || isSaving()"
            [matTooltip]="'waters.form.delete' | translate"
            (click)="delete()">
            @if (isDeleting()) {
              <mat-spinner diameter="18" />
            } @else {
              <span class="flex items-center leading-none"><mat-icon>delete</mat-icon></span>
            }
          </button>
        }
        <button mat-stroked-button [disabled]="isSaving() || isDeleting()" (click)="cancel()">
          {{ 'waters.form.cancel' | translate }}
        </button>
        <button mat-flat-button color="primary" [disabled]="isSaving() || isDeleting()" (click)="save()">
          @if (isSaving()) {
            <mat-spinner diameter="18" />
          } @else {
            {{ 'waters.form.save' | translate }}
          }
        </button>
      </div>
    </div>

    <form [formGroup]="form" class="flex flex-col gap-4">

      <!-- General card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.accommodations.section.general' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.accommodations.section.general-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4 flex flex-col gap-4">
          <!-- Row 1: Number -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <mat-form-field>
              <mat-label>{{ 'waters.accommodations.form.number' | translate }}</mat-label>
              <input matInput type="number" formControlName="number" />
              <mat-error>{{ 'generic.required' | translate }}</mat-error>
            </mat-form-field>
          </div>

          <!-- Row 2: Persons | Spots | Min fishermen | Co-travelers -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <mat-form-field>
              <mat-label>{{ 'waters.accommodations.form.persons' | translate }}</mat-label>
              <input matInput type="number" formControlName="persons" min="0" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'waters.accommodations.form.anglers-count' | translate }}</mat-label>
              <input matInput type="number" formControlName="spots" min="0" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'waters.accommodations.form.minimumFishermen' | translate }}</mat-label>
              <input matInput type="number" formControlName="minimumFishermen" min="0" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'waters.accommodations.form.coTravelers' | translate }}</mat-label>
              <input matInput type="number" formControlName="coTravelers" min="0" />
            </mat-form-field>
          </div>

          <!-- Row 3: DogsAllowed select -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <mat-form-field>
              <mat-label>{{ 'waters.accommodations.form.dogsAllowed' | translate }}</mat-label>
              <mat-select formControlName="dogsAllowed">
                <mat-option value="inherit">{{ 'waters.accommodations.form.dogsAllowed.inherit' | translate }}</mat-option>
                <mat-option [value]="true">{{ 'waters.accommodations.form.dogsAllowed.yes' | translate }}</mat-option>
                <mat-option [value]="false">{{ 'waters.accommodations.form.dogsAllowed.no' | translate }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <!-- Row 4: Toggles -->
          <div class="flex gap-6 flex-wrap">
            <mat-slide-toggle formControlName="isActive">
              {{ 'waters.accommodations.form.isActive' | translate }}
            </mat-slide-toggle>
            <mat-slide-toggle formControlName="exclusive">
              {{ 'waters.accommodations.form.exclusive' | translate }}
            </mat-slide-toggle>
            <mat-slide-toggle formControlName="isFishingSpot">
              {{ 'waters.accommodations.form.isFishingSpot' | translate }}
            </mat-slide-toggle>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Localized names card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.accommodations.section.names' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.accommodations.section.names-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4">
          <app-localized-item-editor [items]="codeListsService.sites()" [group]="namesGroup" />
        </mat-card-content>
      </mat-card>

      <!-- Description card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.accommodations.section.description' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.accommodations.section.description-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4">
          <app-localized-text-editor
            [nl]="form.controls.descriptionNl"
            [en]="form.controls.descriptionEn"
            [de]="form.controls.descriptionDe"
            [it]="form.controls.descriptionIt"
            [fr]="form.controls.descriptionFr"
          />
        </mat-card-content>
      </mat-card>

      <!-- Pricing per combination card. In create mode the unit id is 0 until the form's own save
           returns; the grid is filled in advance and PUT with the new id in the same submit. -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.accommodations.section.pricing' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.accommodations.section.pricing-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4">
          <app-combination-pricing
            [waterId]="combinationWaterId()"
            [unitId]="combinationAccommodationId()"
            unitType="accommodation"
            [spotsControl]="form.controls.spots"
            [minimumFishermenControl]="form.controls.minimumFishermen"
            [coTravelersControl]="form.controls.coTravelers"
          />
        </mat-card-content>
      </mat-card>

      <!-- Location card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.accommodations.section.location' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.accommodations.section.location-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4 flex flex-col gap-4">
          <app-location-picker
            [latControl]="form.controls.latitude"
            [lngControl]="form.controls.longitude"
            [hintLat]="waterHintLat()"
            [hintLng]="waterHintLng()"
          />
          @if (form.hasError('locationRequired') && (form.controls.latitude.touched || form.controls.longitude.touched)) {
            <p class="text-red-600 text-sm">{{ 'waters.accommodations.validation.location-required' | translate }}</p>
          }
        </mat-card-content>
      </mat-card>

      <!-- Image card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.accommodations.section.image' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.accommodations.section.image-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4 flex flex-col gap-4">
          <app-image-picker
            [currentImageName]="imageName()"
            [currentImageUrl]="imageUrl()"
            (fileSelected)="onImageFileSelected($event)"
          />
        </mat-card-content>
      </mat-card>

    </form>
  </div>
}
